import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useHaptic } from '../../hooks/useHaptic';

interface PaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  /** Singular/plural label for the items, e.g. "payment", "alumni profile" */
  itemLabel?: string;
  onPageChange: (page: number) => void;
}

/**
 * Standardised pagination bar — shows item count range, ellipsis-aware
 * page buttons, and prev/next controls. Renders nothing when totalPages < 2.
 */
export function Pagination({
  page,
  totalPages,
  totalItems,
  itemsPerPage,
  itemLabel = 'item',
  onPageChange,
}: PaginationProps) {
  const { lightTap } = useHaptic();

  if (totalPages < 1 || totalItems === 0) return null;

  const handlePage = (p: number) => {
    if (p < 1 || p > totalPages || p === page) return;
    lightTap();
    onPageChange(p);
  };

  // Build page number list with ellipsis
  const buildPages = (): (number | '...')[] => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (page > 3) pages.push('...');
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  const from = Math.min((page - 1) * itemsPerPage + 1, totalItems);
  const to = Math.min(page * itemsPerPage, totalItems);
  const pluralLabel = totalItems === 1 ? itemLabel : `${itemLabel}s`;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100 mt-4">
      {/* Count */}
      <p className="text-xs text-slate-400 font-semibold">
        Showing{' '}
        <span className="font-bold text-slate-800">{from}</span>
        {' '}–{' '}
        <span className="font-bold text-slate-800">{to}</span>
        {' '}of{' '}
        <span className="font-bold text-slate-800">{totalItems}</span>
        {' '}{pluralLabel}
      </p>

      {/* Controls */}
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => handlePage(page - 1)}
          disabled={page === 1}
          aria-label="Previous page"
          className="flex items-center justify-center p-2 rounded-xl border border-slate-200 text-slate-600 hover:border-accent hover:text-accent disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600 transition-smooth cursor-pointer disabled:cursor-not-allowed bg-white"
        >
          <CaretLeft size={14} weight="bold" />
        </button>

        {buildPages().map((p, idx) =>
          p === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-xs text-slate-400 font-bold select-none">
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => handlePage(p)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-smooth cursor-pointer ${
                page === p
                  ? 'bg-accent text-white shadow-md shadow-accent/20'
                  : 'text-slate-600 hover:bg-slate-100 border border-transparent hover:border-slate-200'
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => handlePage(page + 1)}
          disabled={page === totalPages}
          aria-label="Next page"
          className="flex items-center justify-center p-2 rounded-xl border border-slate-200 text-slate-600 hover:border-accent hover:text-accent disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600 transition-smooth cursor-pointer disabled:cursor-not-allowed bg-white"
        >
          <CaretRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
